import DOMPurify from 'dompurify';

const SAFE_CSS_URL =
  /^\s*['"]?\s*(data:(image|font|application\/(font|x-font|octet-stream))[^)]*|#[^)]*)$/i;
const SAFE_URI = /^(?:data:image\/(?:png|jpe?g|gif|bmp|webp|svg\+xml);|#)/i;
const SAFE_LINK = /^(?:https?:|mailto:)/i;

export const ANCHOR_ATTR = 'data-safe-anchor';
export const LINK_ATTR = 'data-safe-href';

const purify = DOMPurify(window);

export const sanitizeCss = (css: string): string =>
  css
    .replace(/@import[^;]*;?/gi, '')
    .replace(/url\(([^)]*)\)/gi, (match, inner: string) =>
      SAFE_CSS_URL.test(inner) ? match : 'none'
    )
    .replace(/expression\s*\(/gi, 'none(')
    .replace(/-moz-binding\s*:/gi, 'x-blocked:');

purify.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName === 'style') {
    data.attrValue = sanitizeCss(data.attrValue);
    return;
  }

  if (node.localName === 'a' && data.attrName === 'href') {
    const value = data.attrValue.trim();
    if (value.startsWith('#')) node.setAttribute(ANCHOR_ATTR, value.slice(1));
    else if (SAFE_LINK.test(value)) node.setAttribute(LINK_ATTR, value);
    data.keepAttr = false;
  }
});

const sanitizeElement = (node: Node): string =>
  purify.sanitize(node, {
    FORBID_TAGS: [
      'style',
      'form',
      'input',
      'button',
      'textarea',
      'select',
      'audio',
      'video',
      'source',
      'track',
    ],
    FORBID_ATTR: ['srcset'],
    ALLOWED_URI_REGEXP: SAFE_URI,
  });

export interface ISanitizedDocx {
  styles: string[];
  html: string;
}

/**
 * Sanitizes nodes produced by docx-preview. `<style>` elements go through
 * `sanitizeCss`, everything else through a dedicated DOMPurify instance.
 */
export const sanitizeDocx = (nodes: Node[]): ISanitizedDocx => {
  const styles: string[] = [];
  const html: string[] = [];

  for (const node of nodes) {
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    if ((node as Element).localName === 'style') {
      styles.push(sanitizeCss(node.textContent ?? ''));
    } else {
      html.push(sanitizeElement(node));
    }
  }

  return { styles, html: html.join('') };
};
